/**
 * Build an OpenSeadragon filter processor that applies a pre-baked 3D LUT
 * (device RGB -> sRGB) with trilinear interpolation.
 *
 * @param {{grid: number, data: Uint8Array}} lut interleaved R,G,B bytes,
 *   indexed by ((B * grid + G) * grid + R) * 3.
 * @returns {(context, callback) => void}
 */
export const createColorProfileFilter = (lut) => {
  const { grid, data } = lut;
  const last = grid - 1;
  const scale = last / 255;
  const g2 = grid * grid;

  return (context, callback) => {
    const imgData = context.getImageData(
      0,
      0,
      context.canvas.width,
      context.canvas.height,
    );
    const px = imgData.data;
    const length = px.length / 4;

    for (let i = 0; i < length; i += 1) {
      const o = i * 4;
      const fr = px[o] * scale;
      const fg = px[o + 1] * scale;
      const fb = px[o + 2] * scale;
      const r0 = Math.floor(fr);
      const g0 = Math.floor(fg);
      const b0 = Math.floor(fb);
      const r1 = r0 >= last ? last : r0 + 1;
      const g1 = g0 >= last ? last : g0 + 1;
      const b1 = b0 >= last ? last : b0 + 1;
      const dr = fr - r0;
      const dg = fg - g0;
      const db = fb - b0;

      const c000 = (b0 * g2 + g0 * grid + r0) * 3;
      const c100 = (b0 * g2 + g0 * grid + r1) * 3;
      const c010 = (b0 * g2 + g1 * grid + r0) * 3;
      const c110 = (b0 * g2 + g1 * grid + r1) * 3;
      const c001 = (b1 * g2 + g0 * grid + r0) * 3;
      const c101 = (b1 * g2 + g0 * grid + r1) * 3;
      const c011 = (b1 * g2 + g1 * grid + r0) * 3;
      const c111 = (b1 * g2 + g1 * grid + r1) * 3;

      for (let c = 0; c < 3; c += 1) {
        const c00 = data[c000 + c] * (1 - dr) + data[c100 + c] * dr;
        const c01 = data[c001 + c] * (1 - dr) + data[c101 + c] * dr;
        const c10 = data[c010 + c] * (1 - dr) + data[c110 + c] * dr;
        const c11 = data[c011 + c] * (1 - dr) + data[c111 + c] * dr;
        const c0 = c00 * (1 - dg) + c10 * dg;
        const c1 = c01 * (1 - dg) + c11 * dg;
        px[o + c] = c0 * (1 - db) + c1 * db;
      }
    }

    context.putImageData(imgData, 0, 0);
    callback();
  };
};
